/**
 * Document Control Page — Phase 1
 * Upload, search, version, audit trail for controlled documents.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import QMSLayout from "../components/QMSLayout";
import {
  FileText, Upload, Search, Filter, Download, Eye, Clock,
  ChevronDown, ChevronRight, Plus, RefreshCw, Loader2,
  Globe, Building2, Tag, History, Shield
} from "lucide-react";

const CLASS_LABELS = {
  software_validation: "Software Validation",
  security_finding_response: "Security Finding Response",
  gap_assessment: "Gap Assessment",
  compliance_report: "Compliance Report",
  audit_report: "Audit Report",
  quality_agreement: "Quality Agreement",
  policy: "Policy",
  procedure: "Procedure",
  record: "Record",
  governance: "Governance",
  remediation: "Remediation",
  annex11: "Annex 11",
  qms: "Quality Management System",
  other: "Other",
};

const GEO_LABELS = {
  global: "Global", us: "US", eu: "EU", br: "Brazil",
  uk: "UK", latam: "LATAM", apac: "APAC", other: "Other",
};

export default function DocumentControlPage() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [filters, setFilters] = useState({ doc_class: "", geography: "", search: "" });
  const [uploadForm, setUploadForm] = useState({ name: "", doc_class: "other", geography: "global", client_id: "", description: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "auditor";

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.doc_class) params.set("doc_class", filters.doc_class);
    if (filters.geography) params.set("geography", filters.geography);
    if (filters.search) params.set("search", filters.search);
    try {
      const res = await fetch(`${API}/doc-control/documents?${params}`, { credentials: "include" });
      const data = await res.json();
      setDocs(data.documents || []);
    } catch { setDocs([]); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadForm.name || !uploadForm.doc_class) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("name", uploadForm.name);
    fd.append("doc_class", uploadForm.doc_class);
    fd.append("geography", uploadForm.geography);
    fd.append("client_id", uploadForm.client_id);
    fd.append("description", uploadForm.description);
    try {
      const res = await fetch(`${API}/doc-control/documents`, { method: "POST", credentials: "include", body: fd });
      if (res.ok) {
        setShowUpload(false);
        setUploadFile(null);
        setUploadForm({ name: "", doc_class: "other", geography: "global", client_id: "", description: "" });
        fetchDocs();
      }
    } catch {}
    setUploading(false);
  };

  const handleDownload = async (docId, rev) => {
    const url = rev ? `${API}/doc-control/documents/${docId}/download?revision=${rev}` : `${API}/doc-control/documents/${docId}/download`;
    const res = await fetch(url, { credentials: "include" });
    if (res.ok) {
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename="(.+)"/);
      a.download = match ? match[1] : `${docId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const loadDetail = async (docId) => {
    const res = await fetch(`${API}/doc-control/documents/${docId}`, { credentials: "include" });
    if (res.ok) setShowDetail(await res.json());
  };

  const loadAuditLog = async (docId) => {
    const url = docId ? `${API}/doc-control/audit-log?doc_id=${docId}` : `${API}/doc-control/audit-log`;
    const res = await fetch(url, { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      setAuditLog(data.logs || []);
      setShowAudit(true);
    }
  };

  return (
    <QMSLayout>
      <div className="space-y-4" data-testid="doc-control-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <FileText size={20} className="text-sky-400" />
              Document Control
            </h1>
            <p className="text-xs text-[#8B95A5] mt-0.5">Controlled document registry with version control and audit trail</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <>
                <button onClick={() => loadAuditLog()} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all" data-testid="audit-log-btn">
                  <History size={12} />Audit Log
                </button>
                <button onClick={() => setShowUpload(!showUpload)} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 transition-all" data-testid="upload-doc-btn">
                  <Plus size={12} />Upload Document
                </button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input
              value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              placeholder="Search documents..."
              className="w-full pl-9 pr-3 py-2 bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded text-sm text-white placeholder-[#555] focus:border-sky-500/30 focus:outline-none"
              data-testid="doc-search-input"
            />
          </div>
          <select value={filters.doc_class} onChange={e => setFilters(f => ({ ...f, doc_class: e.target.value }))}
            className="bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded px-3 py-2 text-sm text-[#CBD5E1]" data-testid="doc-class-filter">
            <option value="">All Classes</option>
            {Object.entries(CLASS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.geography} onChange={e => setFilters(f => ({ ...f, geography: e.target.value }))}
            className="bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded px-3 py-2 text-sm text-[#CBD5E1]" data-testid="doc-geo-filter">
            <option value="">All Geographies</option>
            {Object.entries(GEO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={fetchDocs} className="p-2 rounded bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] text-[#8B95A5] hover:text-white" data-testid="refresh-docs-btn">
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Upload Form */}
        {showUpload && isAdmin && (
          <form onSubmit={handleUpload} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-4 space-y-3" data-testid="upload-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#8B95A5] mb-1 block">Document Name *</label>
                <input value={uploadForm.name} onChange={e => setUploadForm(f => ({ ...f, name: e.target.value }))} required
                  className="w-full px-3 py-2 bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded text-sm text-white focus:border-sky-500/30 focus:outline-none" data-testid="upload-name-input" />
              </div>
              <div>
                <label className="text-xs text-[#8B95A5] mb-1 block">Class *</label>
                <select value={uploadForm.doc_class} onChange={e => setUploadForm(f => ({ ...f, doc_class: e.target.value }))} required
                  className="w-full px-3 py-2 bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded text-sm text-[#CBD5E1]" data-testid="upload-class-select">
                  {Object.entries(CLASS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#8B95A5] mb-1 block">Geography</label>
                <select value={uploadForm.geography} onChange={e => setUploadForm(f => ({ ...f, geography: e.target.value }))}
                  className="w-full px-3 py-2 bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded text-sm text-[#CBD5E1]" data-testid="upload-geo-select">
                  {Object.entries(GEO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#8B95A5] mb-1 block">Client ID</label>
                <input value={uploadForm.client_id} onChange={e => setUploadForm(f => ({ ...f, client_id: e.target.value }))}
                  placeholder="Optional" className="w-full px-3 py-2 bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded text-sm text-white placeholder-[#555] focus:border-sky-500/30 focus:outline-none" data-testid="upload-client-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#8B95A5] mb-1 block">Description</label>
              <input value={uploadForm.description} onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded text-sm text-white focus:border-sky-500/30 focus:outline-none" data-testid="upload-desc-input" />
            </div>
            <div>
              <label className="text-xs text-[#8B95A5] mb-1 block">File (PDF or DOCX) *</label>
              <input type="file" accept=".pdf,.docx" onChange={e => setUploadFile(e.target.files[0])} required
                className="text-sm text-[#CBD5E1]" data-testid="upload-file-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowUpload(false)} className="px-4 py-2 text-sm text-[#8B95A5] hover:text-white">Cancel</button>
              <button type="submit" disabled={uploading} className="flex items-center gap-1.5 px-4 py-2 bg-sky-500/20 text-sky-400 border border-sky-500/30 rounded text-sm hover:bg-sky-500/30 disabled:opacity-40" data-testid="upload-submit-btn">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}Upload
              </button>
            </div>
          </form>
        )}

        {/* Documents Table */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-sky-400" /></div>
        ) : docs.length === 0 ? (
          <div className="text-center py-12 text-[#8B95A5] text-sm" data-testid="no-docs-msg">No controlled documents found.</div>
        ) : (
          <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg overflow-hidden" data-testid="docs-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[rgba(255,255,255,0.07)] text-[#8B95A5] text-xs">
                  <th className="text-left px-4 py-3">ID</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Class</th>
                  <th className="text-left px-4 py-3">Geo</th>
                  <th className="text-left px-4 py-3">Rev</th>
                  <th className="text-left px-4 py-3">Updated</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.doc_id} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)]" data-testid={`doc-row-${d.doc_id}`}>
                    <td className="px-4 py-3 text-[#8B95A5] font-mono text-xs">{d.doc_id}</td>
                    <td className="px-4 py-3 text-white">{d.name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs bg-sky-500/10 text-sky-400 border border-sky-500/20">{CLASS_LABELS[d.doc_class] || d.doc_class}</span></td>
                    <td className="px-4 py-3 text-[#8B95A5] text-xs">{GEO_LABELS[d.geography] || d.geography}</td>
                    <td className="px-4 py-3 text-[#8B95A5]">r{d.current_revision}</td>
                    <td className="px-4 py-3 text-[#8B95A5] text-xs">{d.updated_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => loadDetail(d.doc_id)} className="p-1.5 rounded hover:bg-[rgba(255,255,255,0.05)] text-[#8B95A5] hover:text-white" data-testid={`view-${d.doc_id}`}><Eye size={14} /></button>
                        <button onClick={() => handleDownload(d.doc_id)} className="p-1.5 rounded hover:bg-[rgba(255,255,255,0.05)] text-[#8B95A5] hover:text-sky-400" data-testid={`download-${d.doc_id}`}><Download size={14} /></button>
                        {isAdmin && <button onClick={() => loadAuditLog(d.doc_id)} className="p-1.5 rounded hover:bg-[rgba(255,255,255,0.05)] text-[#8B95A5] hover:text-amber-400" data-testid={`audit-${d.doc_id}`}><History size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Panel */}
        {showDetail && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShowDetail(null)} data-testid="doc-detail-modal">
            <div className="bg-[#181B23] border border-[rgba(255,255,255,0.1)] rounded-lg w-full max-w-xl max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-white mb-1">{showDetail.name}</h2>
              <p className="text-xs text-[#8B95A5] mb-4">{showDetail.doc_id} | r{showDetail.current_revision} | {showDetail.uploaded_by_name}</p>
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div><span className="text-[#8B95A5]">Class:</span> <span className="text-white">{CLASS_LABELS[showDetail.doc_class]}</span></div>
                <div><span className="text-[#8B95A5]">Geography:</span> <span className="text-white">{GEO_LABELS[showDetail.geography]}</span></div>
                <div><span className="text-[#8B95A5]">Client:</span> <span className="text-white">{showDetail.client_id || "N/A"}</span></div>
                <div><span className="text-[#8B95A5]">Size:</span> <span className="text-white">{(showDetail.file_size / 1024).toFixed(1)} KB</span></div>
              </div>
              {showDetail.description && <p className="text-sm text-[#CBD5E1] mb-4">{showDetail.description}</p>}
              <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-1.5"><History size={14} className="text-amber-400" />Revision History</h3>
              <div className="space-y-2">
                {(showDetail.revisions || []).slice().reverse().map(r => (
                  <div key={r.revision} className="flex items-center justify-between p-2 bg-[#1A1D27] rounded text-xs">
                    <div>
                      <span className="text-sky-400 font-mono">r{r.revision}</span>
                      <span className="text-[#8B95A5] ml-2">{r.created_at?.slice(0, 10)}</span>
                      <span className="text-[#CBD5E1] ml-2">{r.notes}</span>
                    </div>
                    <button onClick={() => handleDownload(showDetail.doc_id, r.revision)} className="text-sky-400 hover:text-sky-300" data-testid={`download-rev-${r.revision}`}>
                      <Download size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={() => setShowDetail(null)} className="px-4 py-2 text-sm text-[#8B95A5] hover:text-white">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Audit Log Panel */}
        {showAudit && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShowAudit(false)} data-testid="audit-log-modal">
            <div className="bg-[#181B23] border border-[rgba(255,255,255,0.1)] rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Shield size={18} className="text-amber-400" />Document Control Audit Log</h2>
              {auditLog.length === 0 ? (
                <p className="text-[#8B95A5] text-sm">No audit entries found.</p>
              ) : (
                <div className="space-y-2">
                  {auditLog.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 p-2 bg-[#1A1D27] rounded text-xs">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            log.action === "upload" ? "bg-emerald-500/10 text-emerald-400" :
                            log.action === "download" ? "bg-sky-500/10 text-sky-400" :
                            log.action === "revision" ? "bg-amber-500/10 text-amber-400" :
                            "bg-[rgba(255,255,255,0.05)] text-[#8B95A5]"
                          }`}>{log.action}</span>
                          <span className="text-sky-400 font-mono">{log.doc_id}</span>
                          <span className="text-[#8B95A5]">{log.timestamp?.slice(0, 19).replace("T", " ")}</span>
                        </div>
                        <p className="text-[#CBD5E1] mt-1">{log.detail}</p>
                        <p className="text-[#555] mt-0.5">{log.actor_name} ({log.actor_role})</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end mt-4">
                <button onClick={() => setShowAudit(false)} className="px-4 py-2 text-sm text-[#8B95A5] hover:text-white">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
}
